import { useState } from 'react';
import { cn } from 'cn';
import { Button } from '@/components/ui/button';
import { csrfHeaders, toApiError } from '@/lib/http';
import { REPORT_KINDS, deviceKey } from './reporting';

/**
 * A reader telling us a passage is wrong.
 *
 * **No account, no email.** The reader is doing us a favour; putting a
 * sign-in in front of that loses almost everyone who would have done it.
 *
 * Instead the browser keeps a **device key** and the report's id, which is
 * enough to show that reader the outcome later without knowing who they are.
 * It cannot be used to find them, and clearing the browser loses it — which
 * is the correct trade for a feature that must not require identity.
 */

export function ReportForm({
  khutbahId,
  passageOrdinal,
  language,
  onClose,
  onFiled,
}: {
  khutbahId: string;
  passageOrdinal: number;
  language: string;
  onClose: () => void;
  onFiled: () => void;
}) {
  const [kind, setKind] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setSending(true);
    setError(null);
    try {
      const response = await fetch(
        `/api/public/khutbah/${encodeURIComponent(khutbahId)}/report`,
        {
          method: 'POST',
          headers: {
            ...(await csrfHeaders(new AbortController().signal)),
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            passage: passageOrdinal,
            language,
            kind,
            note: note.trim(),
            deviceKey: deviceKey(),
          }),
        },
      );
      if (!response.ok) throw await toApiError(response);
      onFiled();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not send it.');
    } finally {
      setSending(false);
    }
  }

  const ready = kind !== null && note.trim() !== '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-overlay sm:items-center sm:p-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-title"
        onKeyDown={(event) => {
          if (event.key === 'Escape') onClose();
        }}
        className="flex w-full max-w-[480px] flex-col gap-3 rounded-t-xl border border-border bg-card p-5 sm:rounded-xl"
      >
        <div>
          <h2 id="report-title" className="text-base font-semibold">
            Something wrong with passage {passageOrdinal + 1}?
          </h2>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Thank you — this is the only way we find out.
          </p>
        </div>

        <fieldset>
          <legend className="text-[13px] font-medium">What is wrong?</legend>
          <div className="mt-1.5 flex flex-col gap-1">
            {REPORT_KINDS.map(([value, label]) => (
              <label
                key={value}
                className={cn(
                  // 44px rows: this is a phone format first.
                  'flex min-h-11 items-center gap-2 rounded-md border px-3 text-[13px]',
                  kind === value ? 'border-primary bg-accent' : 'border-border',
                )}
              >
                <input
                  type="radio"
                  name="report-kind"
                  checked={kind === value}
                  onChange={() => setKind(value)}
                />
                {label}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium">What did you notice?</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full resize-y rounded-md border border-border bg-background p-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            size="lg"
            className="h-11"
            disabled={!ready || sending}
            onClick={() => void send()}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>

        {/* Something true, and no more. We are not promising a reply. */}
        <p className="text-xs text-muted-foreground">
          No account needed. We cannot reply, but you can check back on this
          passage to see what happened.
        </p>
      </div>
    </div>
  );
}
